import { getDocumentObject } from '../common/utils/commonUtils'
import { FLAGS_PARAM_NAME } from '../flags/useFlags'
import { SmartSearchConfig } from '../smartSearch/constants'

// Re-export for backward compatibility
export {
  DEFAULT_FUZZY_CONFIG,
  DEFAULT_RRF_CONFIG,
  DEFAULT_SEMANTIC_CONFIG,
  DEFAULT_SMART_SEARCH_CONFIG,
} from '../smartSearch/constants'
export type { SmartSearchConfig } from '../smartSearch/constants'

export const getIsMixpanelDebugEnabled = () => {
  if (getDocumentObject() && typeof localStorage !== undefined) {
    const devFlags = localStorage.getItem(FLAGS_PARAM_NAME)
    return devFlags ? JSON.parse(devFlags).enableMixpanelDebug : false
  }
  return false
}

export const toggleEnableMixpanelDebugWeb = () => {
  if (getDocumentObject() && typeof localStorage !== undefined) {
    const devFlags = localStorage.getItem(FLAGS_PARAM_NAME)
    const enableMixpanelDebug = getIsMixpanelDebugEnabled()

    const newFlags = JSON.parse(devFlags ?? '{}')
    if (enableMixpanelDebug) {
      delete newFlags.enableMixpanelDebug
    } else {
      newFlags.enableMixpanelDebug = true
    }
    localStorage.setItem(FLAGS_PARAM_NAME, JSON.stringify(newFlags))
    window.location.reload()
  }
}

// Smart Search Debug Settings (Web only - uses localStorage)
export interface SmartSearchDebugSettings {
  showDebug: boolean
}

const SMART_SEARCH_DEVTOOLS_KEY = 'devtools-smart-search'

interface SmartSearchDevToolsStorage {
  showDebug?: boolean
  config?: Partial<SmartSearchConfig>
}

const getSmartSearchDevToolsStorage = (): SmartSearchDevToolsStorage => {
  if (getDocumentObject() && typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(SMART_SEARCH_DEVTOOLS_KEY)
    if (stored) {
      try {
        return JSON.parse(stored)
      } catch (error) {
        console.error('Failed to parse Smart Search DevTools storage:', error)
        throw error
      }
    }
  }
  return {}
}

const setSmartSearchDevToolsStorage = (data: SmartSearchDevToolsStorage) => {
  if (getDocumentObject() && typeof localStorage !== 'undefined') {
    localStorage.setItem(SMART_SEARCH_DEVTOOLS_KEY, JSON.stringify(data))
  }
}

export const getSmartSearchDebugSettings = (): SmartSearchDebugSettings => {
  const storage = getSmartSearchDevToolsStorage()
  return {
    showDebug: storage.showDebug ?? true,
  }
}

export const toggleSmartSearchShowDebugWeb = () => {
  const storage = getSmartSearchDevToolsStorage()
  const currentSettings = getSmartSearchDebugSettings()
  storage.showDebug = !currentSettings.showDebug
  setSmartSearchDevToolsStorage(storage)
}

/**
 * Gets the Smart Search DevTools config from localStorage (web only).
 * Returns only explicitly set values or undefined if nothing is set.
 */
export const getSmartSearchConfig = (): Partial<SmartSearchConfig> | undefined => {
  const storage = getSmartSearchDevToolsStorage()
  const config = storage.config
  if (config && Object.keys(config).length > 0) {
    return config
  }
  return undefined
}

/**
 * Sets the Smart Search DevTools config in localStorage (web only).
 */
export const setSmartSearchConfigWeb = (config: SmartSearchConfig) => {
  const storage = getSmartSearchDevToolsStorage()
  storage.config = config
  setSmartSearchDevToolsStorage(storage)
}

/**
 * Resets the Smart Search DevTools config in localStorage (web only).
 */
export const resetSmartSearchConfigWeb = () => {
  const storage = getSmartSearchDevToolsStorage()
  delete storage.config
  setSmartSearchDevToolsStorage(storage)
}
